import React, { useState, useEffect, useCallback } from 'react';
import {
  DashboardStats,
  DashboardChartData,
  DateRangeOption,
  TopProduct,
  LowStockItem,
} from '../types/dashboard';
import {
  getDashboardStats,
  getDashboardChartData,
  getDashboardTopProducts,
  getDashboardLowStock,
} from '../services/supabaseService';
import { StatCard } from '../components/Dashboard/StatCard';
import { RevenueChart } from '../components/Dashboard/RevenueChart';
import { TopProductsTable } from '../components/Dashboard/TopProductsTable';
import { LowStockTable } from '../components/Dashboard/LowStockTable';
import { Coins, TrendingUp, Package, ShoppingCart, Loader2 } from 'lucide-react';
import { AppView, Permissions } from '../components/types';

interface BossDashboardProps {
  userProfile?: {
    role: 'admin' | 'employee';
    access_boss_dashboard?: boolean;
  } | null;
  permissions?: Permissions | null;
  isAdmin?: boolean;
  setView: (view: AppView) => void;
}

const BossDashboard: React.FC<BossDashboardProps> = ({
  userProfile,
  permissions,
  isAdmin = false,
  setView,
}) => {
  // ========== ALL HOOKS FIRST ==========
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [chartData, setChartData] = useState<DashboardChartData[]>([]);
  const [topProducts, setTopProducts] = useState<TopProduct[]>([]);
  const [lowStockItems, setLowStockItems] = useState<LowStockItem[]>([]);
  const [dateRange, setDateRange] = useState<DateRangeOption>('today');
  const [loading, setLoading] = useState<boolean>(true);
  const [authLoading, setAuthLoading] = useState<boolean>(true);

  // Helper function to calculate start and end dates based on dateRange
  const getDateRange = (range: DateRangeOption): { startDate: Date; endDate: Date } => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    let startDate: Date;
    let endDate: Date = new Date(today);
    endDate.setHours(23, 59, 59, 999); // End of today

    switch (range) {
      case 'today':
        startDate = new Date(today);
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'yesterday':
        startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 1);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(startDate);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'week':
        startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 7);
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'month':
        startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 30);
        startDate.setHours(0, 0, 0, 0);
        break;
      default:
        startDate = new Date(today);
        startDate.setHours(0, 0, 0, 0);
    }

    return { startDate, endDate };
  };

  // Fetch data function (useCallback hook)
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { startDate, endDate } = getDateRange(dateRange);

      const [statsData, chartDataResult, topProductsData, lowStockData] = await Promise.all([
        getDashboardStats(startDate, endDate),
        getDashboardChartData(startDate, endDate),
        getDashboardTopProducts(startDate, endDate),
        getDashboardLowStock(),
      ]);

      setStats(statsData);
      setChartData(chartDataResult || []);
      setTopProducts(topProductsData || []);
      setLowStockItems(lowStockData || []);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setStats(null);
      setChartData([]);
      setTopProducts([]);
      setLowStockItems([]);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  // Fetch data on mount and when dateRange changes (useEffect hook)
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // --- SECURITY CHECK (useEffect hook) ---
  useEffect(() => {
    // Wait for auth to load (check if userProfile and permissions are available)
    if (!userProfile && !permissions) {
      setAuthLoading(true);
      return;
    }

    setAuthLoading(false);

    const hasAccess =
      permissions?.hasBossDashboard &&
      (userProfile?.access_boss_dashboard || isAdmin || userProfile?.role === 'admin');

    if (!hasAccess) {
      console.warn('Unauthorized access attempt to Boss Dashboard!');
      setView(AppView.DASHBOARD);
      window.location.hash = '';
    }
  }, [userProfile, permissions, isAdmin, setView]);

  // ========== CALCULATED VALUES (after hooks) ==========
  // Format currency helper (null-safe for API nulls)
  const formatCurrency = (value: number | null | undefined): string => {
    if (value == null || Number.isNaN(value)) return '0 Ft';
    return `${value.toLocaleString('hu-HU')} Ft`;
  };

  // Date range button labels
  const dateRangeLabels: Record<DateRangeOption, string> = {
    today: 'Ma',
    yesterday: 'Tegnap',
    week: '7 nap',
    month: '30 nap',
  };

  // Calculate access (variable only, no return yet)
  const hasAccess =
    permissions?.hasBossDashboard &&
    (userProfile?.access_boss_dashboard || isAdmin || userProfile?.role === 'admin');

  // Skeleton loader component
  const SkeletonLoader = () => (
    <div className="space-y-6">
      {/* Stat Cards Skeleton */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="bg-card p-6 rounded-xl shadow-sm border border-slate-200 dark:border-gray-700 animate-pulse"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
              <div className="w-16 h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
            </div>
            <div className="space-y-2">
              <div className="w-24 h-3 bg-gray-200 dark:bg-gray-700 rounded"></div>
              <div className="w-32 h-8 bg-gray-200 dark:bg-gray-700 rounded"></div>
            </div>
          </div>
        ))}
      </div>
      {/* Chart Skeleton */}
      <div className="bg-card p-6 rounded-xl shadow-sm border border-slate-200 dark:border-gray-700">
        <div className="h-8 w-48 bg-gray-200 dark:bg-gray-700 rounded mb-4 animate-pulse"></div>
        <div className="h-[300px] bg-gray-100 dark:bg-gray-700/50 rounded animate-pulse"></div>
      </div>
      {/* Tables Skeleton */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {[1, 2].map((i) => (
          <div
            key={i}
            className="bg-card p-6 rounded-xl shadow-sm border border-slate-200 dark:border-gray-700 animate-pulse"
          >
            <div className="h-6 w-32 bg-gray-200 dark:bg-gray-700 rounded mb-4"></div>
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((j) => (
                <div key={j} className="h-12 bg-gray-100 dark:bg-gray-700/50 rounded"></div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // ========== EARLY RETURNS (after all hooks) ==========
  // Show loader while checking auth
  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Loader2
            className="animate-spin text-blue-600 dark:text-blue-400 mx-auto mb-4"
            size={32}
          />
          <p className="text-slate-600 dark:text-gray-400">Jogosultság ellenőrzése...</p>
        </div>
      </div>
    );
  }

  // Show loader while fetching data
  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-4 md:p-8">
        <SkeletonLoader />
      </div>
    );
  }

  // Security check: if no access, return null (navigation handled in useEffect)
  if (!hasAccess) {
    return null;
  }

  return (
    <div
      className="max-w-7xl mx-auto p-4 md:p-8 space-y-8 min-h-screen"
      style={{ backgroundColor: 'var(--color-background, #f9fafb)' }}
    >
      {/* Header with Title and Date Range Picker */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-gray-100">Főnök Dashboard</h1>
          <p className="text-slate-500 dark:text-gray-400 text-sm mt-1">
            Üzleti teljesítmény áttekintése
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {(Object.keys(dateRangeLabels) as DateRangeOption[]).map((range) => (
            <button
              key={range}
              onClick={() => setDateRange(range)}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                dateRange === range
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-card text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700'
              }`}
            >
              {dateRangeLabels[range]}
            </button>
          ))}
        </div>
      </div>

      {/* Row 1: Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Bevétel (eladások)"
          value={stats ? formatCurrency(stats.revenue) : '0 Ft'}
          icon={Coins}
          color="green"
          tooltip="A kiválasztott időszak kosár tranzakcióinak összege (kassza)."
        />
        <StatCard
          title="Bruttó Profit"
          value={stats ? formatCurrency(Math.round(stats.gross_profit)) : '0 Ft'}
          icon={TrendingUp}
          color={stats && stats.gross_profit >= 0 ? 'green' : 'red'}
          tooltip="Bevétel mínusz árukészlet költsége (COGS). Csak SALE és CHECKOUT eladások költsége."
        />
        <StatCard
          title="Készlet Értéke"
          value={stats ? formatCurrency(stats.inventory_value) : '0 Ft'}
          icon={Package}
          color="blue"
          tooltip="Jelenlegi készlet értéke: mennyiség × beszerzési ár, EAN-onként a legfrissebb sor."
        />
        <StatCard
          title="Tranzakciók"
          value={
            stats != null && stats.transaction_count != null
              ? stats.transaction_count.toLocaleString('hu-HU')
              : '0'
          }
          icon={ShoppingCart}
          color="blue"
          tooltip="A kosár tranzakciók száma az időszakban."
        />
      </div>

      {/* Row 2: Revenue Chart */}
      <div>
        <RevenueChart data={chartData} />
      </div>

      {/* Row 3: Top Products and Low Stock (2-column grid) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <TopProductsTable products={topProducts} />
        <LowStockTable items={lowStockItems} />
      </div>
    </div>
  );
};

export default BossDashboard;
